import { framedByDefault } from '@utils/frames';
import { useTranslation } from 'next-i18next';

import { FlowConfig } from '../../../providers';
import { usePurchase } from '../../../providers/PurchaseProvider';
import {
  AddPaymentMethod,
  AddPaymentMethodTitle,
  AddShippingAddress,
  FrameStep,
  IntroStep,
  LocaleStep,
  PaymentMethodSelector,
  PaymentMethodTitle,
  PaymentTypeSelector,
  PreAuthDrawer,
  ShippingAddressSelector,
} from '../components';
import { CryptoComStep } from '../components/CryptoComStep';
import { EditShippingAddress } from '../components/shipping-address/EditShippingAddress';
import { DrawByProductId } from '../types';

export const usePreOrderFlow = (
  draw: DrawByProductId,
  product: ProductType,
  isPreOrder: boolean,
): FlowConfig => {
  const { t } = useTranslation('product');
  const purchasingController = usePurchase();

  const { cryptoComEnabled } = draw;

  const drawSupportsFrame =
    purchasingController.frameOptions &&
    purchasingController.frameOptions.length > 0;
  const isFramedByDefault = framedByDefault(
    purchasingController.product?.framingOffered,
  );

  return {
    containerId: 'stripe',
    name: isPreOrder ? 'preOrderFlow' : 'drawFlow',
    steps: [
      ...(isPreOrder
        ? [
            {
              analytics: {
                name: 'intro',
              },
              content: <IntroStep />,
              id: 'intro',
              title: t('preOrder.intro.title'),
            },
          ]
        : []),
      ...(drawSupportsFrame && !isFramedByDefault
        ? [
            {
              analytics: {
                name: 'frame',
              },
              content: <FrameStep product={product} />,
              id: 'frame-intro',
              title: t('drawers.review.framing.title'),
            },
          ]
        : []),
      {
        analytics: {
          name: 'review',
        },
        forceRender: true,
        content: <PreAuthDrawer product={product} />,
        id: 'apply',
        title: t(
          isPreOrder
            ? 'subscriptionCta.publicPreOrder.buttonTitle'
            : 'subscriptionCta.draw.buttonTitle',
        ),
      },
      {
        analytics: {
          name: 'address',
        },
        backStepId: 'apply',
        content: <ShippingAddressSelector />,
        id: 'select_shipping',
        title: t('drawers.review.draw.address.selectAddress.title'),
      },
      {
        analytics: {
          name: 'add address',
        },
        backStepId: 'apply',
        id: 'add_shipping_address',
        content: <AddShippingAddress />,
        title: t('drawers.review.draw.address.addAddress.title'),
      },
      {
        analytics: {
          name: 'edit address',
        },
        backStepId: 'apply',
        id: 'edit_shipping_address',
        content: <EditShippingAddress />,
        title: t('drawers.review.draw.address.editAddress.title'),
      },
      {
        analytics: {
          name: 'payment',
        },
        backStepId: 'apply',
        id: 'select_payment_type',
        content: <PaymentTypeSelector />,
        title: t('drawers.review.draw.paymentMethod.selectPaymentType.title'),
      },
      {
        analytics: {
          name: 'payment',
        },
        backStepId: 'apply',
        id: 'select_payment_method',
        content: <PaymentMethodSelector />,
        title: <PaymentMethodTitle />,
      },
      {
        analytics: {
          name: 'payment',
        },
        forceRender: true,
        backStepId:
          cryptoComEnabled && !isPreOrder ? 'select_payment_type' : 'apply',
        id: 'add_payment_method',
        content: <AddPaymentMethod />,
        title: <AddPaymentMethodTitle />,
      },
      {
        analytics: {
          name: 'cryptoCom',
        },
        forceRender: true,
        backStepId: 'apply',
        id: 'crypto_com',
        content: <CryptoComStep />,
        title: t('drawers.review.draw.paymentMethod.cryptoCom.title'),
      },
      {
        analytics: {
          name: 'locale',
        },
        forceRender: true,
        backStepId: 'apply',
        content: <LocaleStep />,
        id: 'locale',
        title: t('footer:drawers.locale.title'),
      },
      {
        analytics: {
          name: 'frame',
        },
        backStepId: 'apply',
        content: <FrameStep product={product} />,
        id: 'frame',
        title: t('drawers.review.framing.title'),
      },
    ],
  };
};
